// src/App.jsx
// ==============================================
// App.jsx
// Root Application Component with a Centralized, State-Driven Router
// ==============================================

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { authManager } from '@/managers/AuthManager';
import { ConvoManager, useConversation } from '@/managers/ConvoManager';
import { SnippetsManager } from '@/managers/SnippetsManager';
import { PanelManagerProvider } from '@/managers/PanelManager';
import { FileAttachmentProvider } from '@/managers/FileAttachmentManager';
import MainContainer from '@/components/layout/MainContainer';
import NotesManager from '@/managers/NotesManager';
import AddSnippetModal from '@/components/tools/snippets/AddSnippetModal';
import CreateWorkspaceModal from '@/components/layout/RightPanel/shelbulaworkspaces/CreateWorkspaceModal'; // Import the modal
import { canAccessWorkspaces } from '@/utils/workspaceAccess';
import { isWorkspaceBackendEnabled } from '@/utils/setProdOrDev';
import ChatFeedManager from '@/managers/ChatFeedManager/ChatFeedManager';
import TermsAndPrivacyPage from '@/components/auth/TermsAndPrivacyPage';
import { UpgradeResult } from '@/components/layout/settings/UpgradeResult';
import ThemeProvider from '@/managers/ThemeProvider';
import BotAdminDash from '@/components/BotAdminDash';
import { Toaster } from 'react-hot-toast';
import { GlobalOverlay } from '@/managers/GlobalOverlay';
import { AuthContainer } from '@/components/auth/AuthContainer';
import { LandingPage } from '@/components/marketing/LandingPage/LandingPage';
import { botManager } from '@/managers/BotManager';
import { EMPTY_CONVO_ID } from '@/managers/ConvoManager/ConvoActions';
import { keyStateManager } from '@/managers/zKeysManager';


const ADMIN_UID = 'UYWnAh2c48bmarKq6Dpvcr1UWqk2';

const RELOAD_STATUS_MESSAGES = [
  'Reshaping jubigulators...',
  'Eating peanut butter pretzels...',
  'Getting into work mode...',
  <>
    Awaking the <em>friendly</em> bots...
  </>,
];

const useKeyState = () => {
  const [keyState, setKeyState] = useState(() => keyStateManager.getState());

  useEffect(() => {
    const unsubscribe = keyStateManager.subscribe(setKeyState);
    return unsubscribe;
  }, []);

  return keyState;
};

/* ========================
 * ChatRoute - Waits for managers to be ready, then renders MainContainer persistently.
 * Handles initial conversation loading and creation.
 * ARCHITECTURAL FIX: This component is now leaner. It relies solely on `useConversation` 
 * for its state and has no direct subscriptions, preventing unnecessary re-renders.
 * ======================== */
const ChatRoute = () => {
  const { conversationId } = useParams();
  const keyState = useKeyState();
  // We get ALL required state from the central conversation hook. No local state needed.
  const { 
    currentConversationId, 
    currentConversation,
    conversations,
    switchToConversation, 
    createConversation, 
    isNavigating, 
    isInitialized,
  } = useConversation();
  const navigate = useNavigate();
  const hasExplicitConversationRoute = !!conversationId && conversationId !== EMPTY_CONVO_ID;
  const areKeysReady = keyState.isKeysReady;
  const hasKeys = areKeysReady && keyState.hasKeys;

  // The main logic effect. It now correctly depends on all variables it uses.
  useEffect(() => {
    // Wait until the conversation system is fully initialized and we are not in the middle of another navigation event.
    if (!isInitialized || !areKeysReady || isNavigating) {
      return;
    }

    if (!hasKeys) {
      if (conversationId !== EMPTY_CONVO_ID || currentConversationId !== EMPTY_CONVO_ID) {
        createConversation().then(({ success, id }) => {
          if (success && id) {
            navigate(`/chat/${id}`, { replace: true });
          }
        });
      }
      return;
    }

    if (hasExplicitConversationRoute && (conversationId !== currentConversationId || !currentConversation)) {
      switchToConversation(conversationId, { fallbackToNewOnMissing: true });
      return;
    }

    if (conversationId === EMPTY_CONVO_ID || currentConversationId === EMPTY_CONVO_ID) {
      createConversation().then(({ success, id }) => {
        if (success && id && id !== EMPTY_CONVO_ID) {
          navigate(`/chat/${id}`, { replace: true });
        }
      });
      return;
    }

    // Case 2: No real conversation is active — create one (may produce EMPTY_CONVO_ID if no keys yet).
    else if ((!conversationId || conversationId === EMPTY_CONVO_ID) && !currentConversationId) {
      createConversation().then(({ success, id }) => {
        if (success && id) {
          navigate(`/chat/${id}`, { replace: true });
        }
      });
    }
  // This dependency array ensures the effect re-runs if the URL changes or if state dependencies are updated.
  }, [conversationId, currentConversationId, currentConversation, hasExplicitConversationRoute, hasKeys, areKeysReady, isInitialized, isNavigating, switchToConversation, createConversation, navigate]);

  // The guard condition is now simpler, relying on the single `isInitialized` flag from the hook.
  if (!isInitialized) {
    return <GlobalOverlay message="Initializing conversation..." />;
  }
  
  // Once initialized, MainContainer is rendered and stays mounted.
  return <MainContainer />;
};

/* ========================
 * HomeRoute - Redirects to current conversation or creates new one
 * ======================== */
const HomeRoute = () => {
  const keyState = useKeyState();
  const { createConversation, currentConversationId, isInitialized } = useConversation();
  const navigate = useNavigate();
  const areKeysReady = keyState.isKeysReady;
  const hasKeys = areKeysReady && keyState.hasKeys;

  useEffect(() => {
    if (!isInitialized || !areKeysReady) {
        return;
    }
    if (!hasKeys) {
      createConversation().then(({ success, id }) => {
        if (success && id) {
          navigate(`/chat/${id}`, { replace: true });
        }
      });
    } else if (currentConversationId === EMPTY_CONVO_ID) {
      createConversation().then(({ success, id }) => {
        if (success && id && id !== EMPTY_CONVO_ID) {
          navigate(`/chat/${id}`, { replace: true });
        }
      });
    } else if (currentConversationId) {
      navigate(`/chat/${currentConversationId}`, { replace: true });
    } else {
      createConversation().then(({ success, id }) => {
        if (success && id) {
          navigate(`/chat/${id}`, { replace: true });
        }
      });
    }
  }, [createConversation, navigate, currentConversationId, isInitialized, areKeysReady, hasKeys]);

  if (!isInitialized) {
      return <GlobalOverlay message="Initializing..." />;
  }

  return <GlobalOverlay message="Creating new conversation..." />;
};

/* ========================
 * AdminRoute - Protected route for admin users
 * ======================== */
const AdminRoute = React.memo(({ children }) => {
  const authStateRef = React.useRef(authManager.getState());
  const [renderKey, setRenderKey] = React.useState(0);
  
  React.useEffect(() => {
    const unsubscribe = authManager.subscribe((newState) => {
      const currentState = authStateRef.current;
      
      const shouldRerender = (
        newState.isInitialized !== currentState.isInitialized ||
        !!newState.user !== !!currentState.user ||
        newState.user?.uid !== currentState.user?.uid
      );
      
      authStateRef.current = newState;
      
      if (shouldRerender) {
        setRenderKey(prev => prev + 1);
      }
    });
    
    return () => {
      unsubscribe();
    };
  }, []);

  const authState = authStateRef.current;

  if (!authState.isInitialized) {
    return <GlobalOverlay message="Loading..." />;
  }

  const isAuthorized = authState.user && authState.user.uid === ADMIN_UID;

  return isAuthorized ? children : <Navigate to="/" replace />;
});

AdminRoute.displayName = 'AdminRoute';

/* ========================
 * LogoutRoute - Handles user logout
 * ======================== */
const LogoutRoute = React.memo(() => {
  useEffect(() => {
    const performLogout = async () => {
      try {
        localStorage.removeItem('currentConversationId');
        sessionStorage.clear();
        await authManager.signOut();
      } catch (error) {
        // Silent error handling
      }
      window.location.href = '/login'; 
    };
    performLogout();
  }, []);

  return (
    <div className="fixed inset-0 bg-background flex items-center justify-center">
      <div className="text-on-surface">Logging out...</div>
    </div>
  );
});

LogoutRoute.displayName = 'LogoutRoute';

/* ========================
 * StableProviderChain - Wraps app with all necessary providers
 * ======================== */
const StableProviderChain = React.memo(({ children }) => {
  return (
    <PanelManagerProvider>
      <ConvoManager>
        <SnippetsManager>
          <NotesManager>
            <FileAttachmentProvider>
              <ChatFeedManager>
                {children}
              </ChatFeedManager>
            </FileAttachmentProvider>
          </NotesManager>
        </SnippetsManager>
      </ConvoManager>
    </PanelManagerProvider>
  );
});

StableProviderChain.displayName = 'StableProviderChain';

/* ========================
 * HousePortal - Portal for rendering modals
 * ======================== */
const HousePortal = React.memo(({ children }) => {
  const portalRootId = 'portal-root';
  let portalRoot = document.getElementById(portalRootId);

  if (!portalRoot) {
    portalRoot = document.createElement('div');
    portalRoot.id = portalRootId;
    document.body.appendChild(portalRoot);
  }

  return ReactDOM.createPortal(children, portalRoot);
});

HousePortal.displayName = 'HousePortal';

/* ========================
 * AppContent - The core application logic and routing
 * ======================== */
const AppContent = () => {
  // --- State and Initialization Logic ---
  const [authState, setAuthState] = useState(() => authManager.getState());
  const [botState, setBotState] = useState(() => botManager.getState());
  const [error, setError] = useState(null);
  const startupStatusMessage = useMemo(() => {
    const randomIndex = Math.floor(Math.random() * RELOAD_STATUS_MESSAGES.length);
    return RELOAD_STATUS_MESSAGES[randomIndex];
  }, []);

  const initializeManagers = useCallback(async () => {
    try {
        setError(null);
        if (!botManager.getState().isInitialized) {
            await botManager.initialize(authManager);
        }
    } catch (e) {
        console.error("Critical initialization failure:", e);
        setError(e);
    }
}, []);

  useEffect(() => {
    initializeManagers();
    const unsubAuth = authManager.subscribe(setAuthState);
    const unsubBot = botManager.subscribe(setBotState);
    return () => {
      unsubAuth();
      unsubBot();
    };
  }, [initializeManagers]);

  // --- Modal Logic ---
  const [isSnippetModalOpen, setIsSnippetModalOpen] = useState(false);
  const [snippetModalContent, setSnippetModalContent] = useState('');
  const [snippetModalLanguage, setSnippetModalLanguage] = useState('');
  const [isCreateWorkspaceModalOpen, setCreateWorkspaceModalOpen] = useState(false); // State for workspace modal

  const handleOpenSnippetModal = useCallback((event) => {
    const { code, language } = event.detail || {};
    setSnippetModalContent(code || '');
    setSnippetModalLanguage(language || '');
    setIsSnippetModalOpen(true);
  }, []);

  const handleCloseSnippetModal = useCallback(() => {
    setIsSnippetModalOpen(false);
    setSnippetModalContent('');
    setSnippetModalLanguage('');
  }, []);
  
  const handleOpenCreateWorkspaceModal = useCallback(() => {
    if (!canAccessWorkspaces(authManager.getState().user) || !isWorkspaceBackendEnabled()) {
      return;
    }
    setCreateWorkspaceModalOpen(true);
  }, []);
  
  const handleCloseCreateWorkspaceModal = useCallback(() => {
    setCreateWorkspaceModalOpen(false);
  }, []);

  useEffect(() => {
    window.addEventListener('open-add-snippet-modal', handleOpenSnippetModal);
    window.addEventListener('open-create-workspace-modal', handleOpenCreateWorkspaceModal);
    return () => {
      window.removeEventListener('open-add-snippet-modal', handleOpenSnippetModal);
      window.removeEventListener('open-create-workspace-modal', handleOpenCreateWorkspaceModal);
    };
  }, [handleOpenSnippetModal, handleOpenCreateWorkspaceModal]);

  // --- Render Logic ---
  const isAppLoading = !authState.isInitialized || botState.isLoading || !botState.isInitialized;
  const isUserLoggedIn = !!authState.user;
  
  if (error) {
    return <GlobalOverlay error={error} onRetry={initializeManagers} />;
  }

  if (isAppLoading) {
    let message = "Assembling Gigatrons...";
    let progress = 30;
    if (!authState.isInitialized) {
      message = startupStatusMessage;
      progress = 30;
    } else if (botState.isLoading) {
      message = "Loading Environment...";
      progress = 60;
    } else if (!botState.isInitialized) {
      message = "Finalizing Setup...";
      progress = 80;
    }
    return <GlobalOverlay message={message} progress={progress} />;
  }

  return (
    <StableProviderChain>
      <Toaster position="top-right" />

      <HousePortal>
        {isSnippetModalOpen && (
          <AddSnippetModal
            content={snippetModalContent}
            language={snippetModalLanguage}
            isOpen={isSnippetModalOpen}
            onClose={handleCloseSnippetModal}
          />
        )}
        {isCreateWorkspaceModalOpen && canAccessWorkspaces(authState.user) && isWorkspaceBackendEnabled() && (
          <CreateWorkspaceModal
            isOpen={isCreateWorkspaceModalOpen}
            onClose={handleCloseCreateWorkspaceModal}
          />
        )}
      </HousePortal>
      
      <Routes>
        {isUserLoggedIn ? (
          // --- PROTECTED ROUTES ---
          <>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/chat/:conversationId" element={<ChatRoute />} />
            <Route path="/chat" element={<ChatRoute />} />
            <Route path="/logout" element={<LogoutRoute />} />
            <Route path="/upgraderesult" element={<UpgradeResult />} />
            <Route 
              path="/botdashery" 
              element={<AdminRoute><BotAdminDash /></AdminRoute>} 
            />
            {/* Redirect public auth routes to home if user is already logged in */}
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/signup" element={<Navigate to="/" replace />} />
          </>
        ) : (
          // --- PUBLIC ROUTES ---
          <>
            <Route path="/login" element={<AuthContainer initialMode="signin" />} />
            <Route path="/signup" element={<AuthContainer initialMode="signup" />} />
            {/* For unauthenticated users, all other paths lead to the landing page */}
            <Route path="*" element={<LandingPage />} />
          </>
        )}
        {/* --- COMMON ROUTES (available to all) --- */}
        <Route path="/terms" element={<TermsAndPrivacyPage />} />
        <Route path="/privacy" element={<TermsAndPrivacyPage />} />
      </Routes>
    </StableProviderChain>
  );
};

/* ========================
 * App - Main application component
 * ======================== */
function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export { HousePortal };
export default App;